import { Helmet } from "react-helmet-async";
import { defaultSEOConfig, type SEOConfig } from "./seo-config";

interface SEOProps extends Partial<SEOConfig> {
  children?: React.ReactNode;
  /**
   * The LCP image URL for the current page (used for dynamic preload)
   */
  lcpImage?: string;
}

export function SEO({
  title,
  description,
  canonical,
  openGraph,
  twitter,
  children,
  lcpImage,
}: SEOProps) {
  const seo = {
    title: title || defaultSEOConfig.title,
    titleTemplate: defaultSEOConfig.titleTemplate,
    description: description || defaultSEOConfig.description,
    canonical: canonical,
    openGraph: {
      ...defaultSEOConfig.openGraph,
      ...openGraph,
      title: openGraph?.title || title || defaultSEOConfig.openGraph.title,
      description: openGraph?.description || description || defaultSEOConfig.openGraph.description,
    },
    twitter: {
      ...defaultSEOConfig.twitter,
      ...twitter,
    },
  };

  const isHomePage = typeof window !== 'undefined' ? window.location.pathname === '/' : false;
  const isBlogPage = typeof window !== 'undefined' ? window.location.pathname.startsWith('/blog') : false;

  // Generate responsive preload links for LCP image
  function renderLcpPreloads(imageUrl: string | undefined) {
    if (!imageUrl) return null;
    // Derive base name and dir
    const getBaseName = (src: string) => src.split('/').pop()?.replace(/\.[^/.]+$/, '') || '';
    const getDir = (src: string) => {
      const parts = src.split('/');
      parts.pop();
      return parts.join('/') + '/';
    };
    const baseName = getBaseName(imageUrl);
    const dir = getDir(imageUrl);
    const breakpoints = [
      { suffix: '-mobile', width: 320 },
      { suffix: '-tablet', width: 768 },
      { suffix: '-desktop', width: 1024 },
      { suffix: '-xl', width: 1920 },
    ];
    // Determine extension
    const originalExt = imageUrl.split('.').pop()?.toLowerCase() || 'jpg';
    const isPng = originalExt === 'png';
    const fallbackExt = isPng ? 'png' : 'jpg';
    // Build srcSet strings
    const webpSrcSet = breakpoints.map(bp => `${dir}${baseName}${bp.suffix}.webp ${bp.width}w`).join(', ');
    const fallbackSrcSet = breakpoints.map(bp => `${dir}${baseName}${bp.suffix}.${fallbackExt} ${bp.width}w`).join(', ');
    // Preload the smallest (mobile) variant for both formats
    return (
      <>
        <link rel="preload" as="image" href={`${dir}${baseName}-mobile.webp`} imagesrcset={webpSrcSet} imagesizes="100vw" type="image/webp" />
        <link rel="preload" as="image" href={`${dir}${baseName}-mobile.${fallbackExt}`} imagesrcset={fallbackSrcSet} imagesizes="100vw" type={isPng ? 'image/png' : 'image/jpeg'} />
      </>
    );
  }

  return (
    <Helmet>
      {/* General tags */}
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Favicon tags */}
      <link rel="icon" href="/favicon.ico" sizes="any" />
      <link rel="icon" type="image/svg+xml" href="/images/brand/logo.svg" />
      <link rel="apple-touch-icon" sizes="180x180" href="/images/brand/apple-touch-icon.png" />
      <link rel="icon" type="image/png" sizes="32x32" href="/images/brand/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/images/brand/favicon-16x16.png" />
      <link rel="manifest" href="/site.webmanifest" />
      <meta name="theme-color" content="#18181b" />

      {/* Preload LCP hero image for home page only */}
      {isHomePage && (
        <>
          <link rel="preload" as="image" href="/images/eco-cabin-hero-mobile.webp" imagesrcset="/images/eco-cabin-hero-mobile.webp 320w, /images/eco-cabin-hero-tablet.webp 768w, /images/eco-cabin-hero-desktop.webp 1024w, /images/eco-cabin-hero-xl.webp 1920w" imagesizes="100vw" type="image/webp" />
          <link rel="preload" as="image" href="/images/eco-cabin-hero-mobile.jpg" imagesrcset="/images/eco-cabin-hero-mobile.jpg 320w, /images/eco-cabin-hero-tablet.jpg 768w, /images/eco-cabin-hero-desktop.jpg 1024w, /images/eco-cabin-hero-xl.jpg 1920w" imagesizes="100vw" type="image/jpeg" />
        </>
      )}
      {/* Preload LCP image for blog page dynamically */}
      {isBlogPage && lcpImage && renderLcpPreloads(lcpImage)}

      {/* OpenGraph tags */}
      <meta property="og:type" content={seo.openGraph.type} />
      <meta property="og:title" content={seo.openGraph.title} />
      <meta property="og:description" content={seo.openGraph.description} />
      <meta property="og:url" content={seo.openGraph.url} />
      <meta property="og:site_name" content={seo.openGraph.siteName} />
      {seo.openGraph.images?.map((image, index) => (
        <meta
          key={`og:image:${index}`}
          property="og:image"
          content={image.url}
        />
      ))}

      {/* Twitter Card tags */}
      <meta name="twitter:card" content={seo.twitter.cardType} />
      <meta name="twitter:site" content={seo.twitter.site} />
      <meta name="twitter:creator" content={seo.twitter.handle} />
      <meta name="twitter:title" content={seo.openGraph.title} />
      <meta name="twitter:description" content={seo.openGraph.description} />
      {seo.openGraph.images?.[0] && (
        <meta name="twitter:image" content={seo.openGraph.images[0].url} />
      )}

      {children}
    </Helmet>
  );
} 